import { useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { addDays, todayKey } from '../../calendar/dates';
import { extendCase } from '../api';
import type { AfterSalesRecord } from '../types';
import styles from '../create/create.module.css';

export interface ExtendDialogProps {
  record: AfterSalesRecord;
  onDone: () => Promise<void>;
  onClose: () => void;
}

/** „Frist verlängern" (After-Sales 04; the dialog has no frame, own texts): the new external deadline. */
export function ExtendDialog({ record, onDone, onClose }: ExtendDialogProps) {
  const formId = useId();
  const [deadlineOn, setDeadlineOn] = useState(addDays(todayKey(), 7));
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!deadlineOn) {
      setError('Bitte ein Datum angeben.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      await extendCase(record.id, deadlineOn);
      await onDone();
      onClose();
    } catch (caught) {
      const field = caught instanceof ApiError ? caught.fields['deadlineOn'] : undefined;
      if (field) setError(field);
      else setBanner(errorMessage(caught));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={480}
      eyebrow={record.numberLabel}
      title="Frist verlängern"
      subtitle={
        record.assigned.deadline
          ? `Bisher bis ${record.assigned.deadline}. Die Erinnerung bei Ablauf läuft mit der neuen Frist.`
          : 'Die Erinnerung bei Ablauf läuft mit der neuen Frist.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Frist verlängern
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <TextField
          label="Neue Frist"
          type="date"
          required
          value={deadlineOn}
          onChange={(event) => {
            setDeadlineOn(event.target.value);
            setError(null);
          }}
          error={error ?? undefined}
        />
      </form>
    </Dialog>
  );
}
